/**
 * Shared comparison layout for side-by-side existing vs modified portfolio views.
 * Used by snapshot components (X-ray, allocation) in followup mode.
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./comparison-layout.i18n";

/**
 * The side headers every followup comparison uses — exported so components
 * with their own table (QL-416 cost/benefit) read the same words as the
 * shared grid on the same page.
 */
export function useComparisonSideLabels(variant: "portfolio" | "savingsPlan" = "portfolio"): {
  existing: string;
  modified: string;
} {
  const t = useT(T);
  return variant === "savingsPlan"
    ? { existing: t.existingSavingsPlan, modified: t.modifiedSavingsPlan }
    : { existing: t.existingPortfolio, modified: t.modifiedPortfolio };
}

type ComparisonLayoutProps = {
  existingContent: React.ReactNode;
  modifiedContent: React.ReactNode;
  /** Agreement follow-ups name the savings plan; account follow-ups keep "Portfolio". */
  variant?: "portfolio" | "savingsPlan";
  children?: React.ReactNode;
};

export function ComparisonLayout({
  existingContent,
  modifiedContent,
  variant = "portfolio",
  children,
}: ComparisonLayoutProps) {
  const t = useT(T);
  const existingLabel = variant === "savingsPlan" ? t.existingSavingsPlan : t.existingPortfolio;
  const modifiedLabel = variant === "savingsPlan" ? t.modifiedSavingsPlan : t.modifiedPortfolio;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <h5 className="text-sm font-medium text-center text-muted-foreground">{existingLabel}</h5>
          {existingContent}
        </div>
        <div className="space-y-2">
          <h5 className="text-sm font-medium text-center text-muted-foreground">{modifiedLabel}</h5>
          {modifiedContent}
        </div>
      </div>
      {children}
    </div>
  );
}

type ComparisonTableRow = {
  id: string;
  label: string;
  color?: string;
  existingValue: number;
  modifiedValue: number;
};

type ComparisonTableProps = {
  existingData: Array<{ id: string; label: string; value: number; color?: string }>;
  modifiedData: Array<{ id: string; label: string; value: number; color?: string }>;
  categoryLabel: string;
  variant?: "portfolio" | "savingsPlan";
};

export function ComparisonTable({
  existingData,
  modifiedData,
  categoryLabel,
  variant = "portfolio",
}: ComparisonTableProps) {
  const t = useT(T);
  const existingLabel = variant === "savingsPlan" ? t.existingSavingsPlan : t.existingPortfolio;
  const modifiedLabel = variant === "savingsPlan" ? t.modifiedSavingsPlan : t.modifiedPortfolio;

  // Merge both datasets
  const mergedMap = new Map<string, ComparisonTableRow>();
  for (const item of existingData) {
    mergedMap.set(item.id, {
      id: item.id,
      label: item.label,
      color: item.color,
      existingValue: item.value,
      modifiedValue: 0,
    });
  }
  for (const item of modifiedData) {
    const existing = mergedMap.get(item.id);
    if (existing) {
      existing.modifiedValue = item.value;
      existing.color = existing.color ?? item.color;
    } else {
      mergedMap.set(item.id, {
        id: item.id,
        label: item.label,
        color: item.color,
        existingValue: 0,
        modifiedValue: item.value,
      });
    }
  }

  // Sort like the single-view tables: largest allocation first (modified takes precedence).
  const rows = Array.from(mergedMap.values()).sort(
    (a, b) => b.modifiedValue - a.modifiedValue || b.existingValue - a.existingValue,
  );

  if (rows.length === 0) return null;

  const hasColors = rows.some((row) => row.color);

  return (
    <Table variant="data">
      <TableHeader>
        <TableRow>
          {hasColors && <TableHead className="w-12"></TableHead>}
          <TableHead>{categoryLabel}</TableHead>
          <TableHead className="text-right">{existingLabel}</TableHead>
          <TableHead className="text-right">{modifiedLabel}</TableHead>
          <TableHead className="text-right">Δ</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          // Round before deriving the sign so float dust doesn't render "+0.0%"/"-0.0%"
          // (the `+ 0` folds negative zero into plain zero).
          const diff = Math.round((row.modifiedValue - row.existingValue) * 10) / 10 + 0;
          return (
            <TableRow key={row.id}>
              {hasColors && (
                <TableCell>
                  {row.color && (
                    <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: row.color }} />
                  )}
                </TableCell>
              )}
              <TableCell>{row.label}</TableCell>
              <TableCell className="text-right">{row.existingValue.toFixed(1)}%</TableCell>
              <TableCell className="text-right">{row.modifiedValue.toFixed(1)}%</TableCell>
              <TableCell
                className={`text-right font-medium ${
                  diff > 2
                    ? "text-success"
                    : diff < -2
                      ? "text-destructive"
                      : "text-muted-foreground"
                }`}
              >
                {diff > 0 ? "+" : ""}
                {diff.toFixed(1)}%
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
